import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function PrivacyPolicy() {
  const classes = useStyles();
  const seo = useSEO("privacy-policy");

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          Privacy Policy
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Last updated: October 17, 2025
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          Sugar Cribs ("we," "us," or "our") is committed to protecting your
          privacy. This Privacy Policy explains how we collect, use,
          disclose, and safeguard your information when you visit our
          website sugarcribs.com and use our services (collectively, the
          "Service"). Please read this Privacy Policy carefully. If you do
          not agree with the terms of this Privacy Policy, please do not
          access the Service.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          1. Information We Collect
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>1.1 Personal Information</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may collect personally identifiable information ("Personal
          Information") that you voluntarily provide to us when you register
          for an account, upload content, make purchases, or interact with
          the Service. This information may include:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Name</li>
          <li className={classes.listItem}>Email address</li>
          <li className={classes.listItem}>Phone number</li>
          <li className={classes.listItem}>Mailing address</li>
          <li className={classes.listItem}>Payment information</li>
          <li className={classes.listItem}>
            Biometric information (e.g., facial recognition data for
            identity verification)
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Biometric Information</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We collect biometric identifiers, such as facial recognition data, solely 
          for age and identity verification purposes under 18 U.S.C. 2257 and KYC/AML 
          regulations. Biometric data is encrypted, securely stored, and permanently 
          deleted after verification or within 30 days of account closure, 
          whichever occurs first.
        </Typography>
        
        <Typography variant="body1" className={classes.paragraph}>
          <strong>1.2 Non-Personal Information</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may collect non-personally identifiable information
          ("Non-Personal Information") about you when you interact with the
          Service. This information may include:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Browser type</li>
          <li className={classes.listItem}>IP address</li>
          <li className={classes.listItem}>Device type</li>
          <li className={classes.listItem}>Operating system</li>
          <li className={classes.listItem}>
            Usage data (e.g., pages viewed, time spent on the Service)
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>1.3 Cookies and Tracking Technologies</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We use cookies, web beacons, local storage, and similar technologies 
          to collect information about your interactions with the Service. 
          Cookies help us operate securely, remember your preferences, 
          and analyze performance.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Types of cookies we use:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}><strong>Essential Cookies:</strong> Required for core functionality (login, payments, security).</li>
          <li className={classes.listItem}><strong>Analytics Cookies:</strong> Used to understand usage patterns via tools such as Google Analytics and Firebase.</li>
          <li className={classes.listItem}><strong>Functional Cookies:</strong> Remember preferences like language and region.</li>
          <li className={classes.listItem}><strong>Third-Party Cookies:</strong> Set by partners (e.g., CCBill) for fraud prevention or analytics.</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          You can manage or delete cookies through your browser settings. 
          Some features may not function properly without cookies.
        </Typography>
        
        <Typography variant="h5" className={classes.subtitle}>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>2.1 Personal Information</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may use your Personal Information to:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Provide and manage the Service</li>
          <li className={classes.listItem}>
            Process transactions and send you related information, including
            purchase confirmations and invoices
          </li>
          <li className={classes.listItem}>
            Verify your identity and comply with legal requirements (e.g.,
            18 U.S.C. 2257)
          </li>
          <li className={classes.listItem}>Respond to your comments, questions, and requests</li>
          <li className={classes.listItem}>
            Communicate with you about updates, promotions, and news about
            the Service
          </li>
          <li className={classes.listItem}>Improve the Service and develop new features</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          Identity verification and anti-money laundering checks are processed 
          in accordance with our KYC / AML Compliance Policy.
        </Typography>
        
        <Typography variant="body1" className={classes.paragraph}>
          <strong>2.2 Non-Personal Information</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may use Non-Personal Information for various purposes,
          including:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Analyzing usage patterns and trends</li>
          <li className={classes.listItem}>Administering the Service and diagnosing technical issues</li>
          <li className={classes.listItem}>Enhancing the user experience</li>
          <li className={classes.listItem}>Gathering demographic information for aggregate use</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>2.3 International and Legal Compliance</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Sugar Cribs complies with applicable U.S. and international data 
          protection laws, including the General Data Protection Regulation 
          (GDPR) and UK Data Protection Act 2018. For visitors located in the 
          European Economic Area (EEA), the United Kingdom, or Canada, our 
          legal bases for processing personal data include:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Your consent (e.g., for optional cookies or marketing);</li>
          <li className={classes.listItem}>Contract performance (e.g., providing platform access and payouts);</li>
          <li className={classes.listItem}>Legal obligations (e.g., AML/KYC verification, 18 U.S.C. 2257);</li>
          <li className={classes.listItem}>Legitimate interests (e.g., fraud prevention, security, and analytics).</li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          3. How We Share Your Information
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>3.1 Service Providers</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may share your information with third-party service providers
          who perform services on our behalf, such as payment processing
          (e.g., CCBill), data analysis, email delivery, and customer
          service.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>3.2 Legal Requirements</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may disclose your information if required to do so by law or in
          response to valid requests by public authorities (e.g., a court or
          a government agency).
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>3.3 Business Transfers</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If we are involved in a merger, acquisition, or sale of all or a
          portion of our assets, your information may be transferred as part
          of that transaction.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>3.4 Protection of Rights</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may disclose your information to protect and defend our rights
          or property, prevent fraud, and enforce our Terms and Conditions.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          4. Your Rights and Choices
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>4.1 Access and Update</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You have the right to access and update your Personal Information
          at any time by logging into your account and editing your profile.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>4.2 Data Retention</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We will retain your information for as long as your account is
          active or as needed to provide you services. If you wish to cancel
          your account or request that we no longer use your information to
          provide you services, please contact us at{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          . We will retain and use your information as necessary to comply
          with our legal obligations, resolve disputes, and enforce our
          agreements.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>4.2.1 Data Transfers</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may transfer your data to servers located in the United States 
          or other jurisdictions where our processors operate. We take 
          reasonable measures to ensure your data receives an equivalent 
          level of protection consistent with applicable laws.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>4.2.2 Data Retention</strong>
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}><strong>Account information:</strong> retained while your account is active.</li>
          <li className={classes.listItem}><strong>Payment and transaction data:</strong> retained for 7 years for legal and audit compliance.</li>
          <li className={classes.listItem}><strong>Verification and biometric data:</strong> deleted within 30 days after verification or account closure.</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>4.3 Opt-Out</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You can opt-out of receiving promotional emails from us by
          following the unsubscribe instructions in those emails or by
          contacting us at{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          . If you opt-out, we may still send you non-promotional emails,
          such as those about your account or our ongoing business
          relations.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>4.4 Do Not Track</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Your browser may offer a "Do Not Track" option, which allows you
          to signal to operators of websites and web applications and
          services that you do not wish such operators to track certain of
          your online activities over time and across different websites.
          The Service does not support Do Not Track requests at this time.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          5. California Consumer Privacy Act (CCPA) Rights
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you are a resident of California, you have certain rights under
          the CCPA. These rights include:
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>5.1 Right to Know</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You have the right to request that we disclose what Personal
          Information we collect, use, disclose, and sell. To exercise this
          right, please contact us at{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          .
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>5.2 Right to Delete</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You have the right to request the deletion of your Personal
          Information that we have collected, subject to certain exceptions.
          To exercise this right, please contact us at{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          .
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>5.3 Right to Opt-Out</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You have the right to opt-out of the sale of your Personal
          Information. Sugar Cribs does not sell Personal Information;
          however, if this policy changes, we will update this section
          accordingly and provide a mechanism to opt-out.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>5.4 Non-Discrimination</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You have the right to non-discriminatory treatment for exercising
          any of your CCPA rights. Sugar Cribs will not discriminate against
          you for exercising your CCPA rights.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>5.5 Authorized Agent</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You may designate an authorized agent to make a request under the
          CCPA on your behalf. To designate an authorized agent, please
          provide written authorization and verify your identity directly
          with us.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>5.6 Verification</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          When you make a request to exercise your CCPA rights, we may need
          to verify your identity before processing your request. This may
          include requesting additional information to confirm your
          identity.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          6. Security of Your Information
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We use administrative, technical, and physical security measures
          to help protect your Personal Information. While we have taken
          reasonable steps to secure the Personal Information you provide to
          us, please be aware that despite our efforts, no security measures
          are perfect or impenetrable, and no method of data transmission
          can be guaranteed against any interception or other types of
          misuse.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          7. Third-Party Links
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Service may contain links to third-party websites or services
          that are not owned or controlled by Sugar Cribs. We are not
          responsible for the privacy practices or the content of these
          third-party websites or services. Please review the privacy
          policies of these third-party websites or services before
          providing any information to them.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          8. Children's Privacy
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Service is not intended for individuals under the age of 18.
          We do not knowingly collect Personal Information from individuals
          under 18. If we become aware that we have collected Personal
          Information from a child under age 18, we will take steps to
          delete such information from our files as soon as possible.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>8.1 Cookies and Consent</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We display a cookie consent banner when you first visit the Service, 
          allowing you to accept, reject, or manage cookie preferences. 
          By continuing to use the Service, you consent to the placement 
          of cookies in accordance with this Privacy Policy.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          9. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may update this Privacy Policy from time to time. If we make
          material changes to this Privacy Policy, we will notify you by
          updating the effective date of this Privacy Policy and posting it
          on the Service. We encourage you to review this Privacy Policy
          periodically to stay informed about how we are protecting your
          information.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          10. Contact Us
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you have any questions or concerns about this Privacy Policy or
          our data practices, please contact us at:
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>TOPV1 LLC (d/b/a "Sugar Cribs")</strong>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Email: <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            732 S. 6th Street, Suite N, Las Vegas, NV 89101, USA (Registered Office)
          </Typography>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          By using the Service, you acknowledge that you have read,
          understood, and agree to be bound by this Privacy Policy.
        </Typography>
      </Container>
    </>
  );
}
